import React, { useEffect, useState } from 'react';
import { authStorage } from '../../utils/storage';
import { X, Clock, User, Database, Activity, ArrowRight, FileText } from 'lucide-react';

const OP_COLORS = {
  UPDATE: { bg: 'var(--bg-accent-soft)', color: 'var(--accent)' },
  CREATE: { bg: '#d1fae5', color: '#065f46' },
  DELETE: { bg: '#fee2e2', color: '#991b1b' },
};

export default function AuditLogModal({ unitId, onClose }) {
  const token = authStorage.getToken();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true);
      try {
        const res = await fetch(`/api/units/${unitId}/audit-log?limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!ignore) {
          setItems(Array.isArray(json.items) ? json.items : []);
          setTotal(json.total || 0);
          if (json.items?.length > 0) setSelected(json.items[0]);
        }
      } catch (e) {
        if (!ignore) setError('Failed to load audit log');
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    if (unitId && token) load();
    return () => { ignore = true; };
  }, [unitId, token]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function ts(t) {
    try {
      return new Date(t).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return String(t); }
  }

  function formatFieldName(key) {
    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2')   // camelCase → "Camel Case"
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // e.g. "PHONENumber" → "PHONE Number"
      .replace(/_/g, ' ')
      .replace(/^bu /i, '')
      .trim()
      .split(/\s+/)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  function DiffView({ data }) {
    if (!data || typeof data !== 'object') return <span style={{ color: 'var(--fg-3)', fontSize: 13 }}>No data</span>;
    let oldData = data.old || {};
    let newData = data.new || {};
    if (!data.old && !data.new) {
      // Older entries (written before before/after snapshots) stored the submitted
      // values directly. Show those as "empty -> value" so the entry isn't blank.
      const flat = data.fields || data.updates || null;
      if (flat && typeof flat === 'object') {
        oldData = {};
        newData = {};
        Object.entries(flat).forEach(([k, v]) => { if (v != null && typeof v !== 'object') newData[k] = v; });
      }
      if (Object.keys(newData).length === 0) return <span style={{ color: 'var(--fg-3)', fontSize: 13 }}>No diff data</span>;
    }

    const changes = [];
    new Set([...Object.keys(oldData), ...Object.keys(newData)]).forEach(key => {
      const oldVal = oldData[key];
      const newVal = newData[key];
      if (oldVal == null && newVal == null) return;
      if (String(oldVal) === String(newVal)) return;
      changes.push({ key, label: formatFieldName(key), oldVal, newVal });
    });

    if (changes.length === 0) return <span style={{ color: 'var(--fg-3)', fontSize: 13 }}>No changes detected</span>;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {changes.map(c => (
          <div key={c.key} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border-subtle)', alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--fg-2)' }}>
              {c.label}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 999, background: '#fee2e2', color: '#991b1b', fontSize: 12, fontWeight: 500 }}>
                {c.oldVal === null || c.oldVal === undefined ? 'empty' : String(c.oldVal)}
              </span>
              <ArrowRight size={12} style={{ color: 'var(--fg-3)', flexShrink: 0 }} />
              <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 999, background: '#d1fae5', color: '#065f46', fontSize: 12, fontWeight: 500 }}>
                {c.newVal === null || c.newVal === undefined ? 'empty' : String(c.newVal)}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const opStyle = (op = '') => OP_COLORS[op.toUpperCase()] || { bg: 'var(--bg-sunken)', color: 'var(--fg-2)' };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)',
          width: '100%', maxWidth: 900, height: '80vh', maxHeight: 680,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
          <Activity size={16} style={{ color: 'var(--accent)' }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Audit Log</div>
            <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>
              {loading ? 'Loading…' : `${total} ${total === 1 ? 'entry' : 'entries'} found`}
            </div>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-3)', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Body: two-pane */}
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', flex: 1, overflow: 'hidden' }}>

          {/* List pane */}
          <div style={{ borderRight: '1px solid var(--border-subtle)', overflowY: 'auto', padding: '8px 0' }}>
            {loading && <div style={{ padding: 20, textAlign: 'center', color: 'var(--fg-3)', fontSize: 13.5 }}>Loading…</div>}
            {error && <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-danger)', fontSize: 13.5 }}>{error}</div>}
            {!loading && !error && items.map(it => {
              const isSel = selected?.id === it.id;
              return (
              <div
                key={it.id}
                onClick={() => setSelected(it)}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  background: isSel ? 'color-mix(in srgb, var(--accent) 13%, var(--bg-surface))' : 'transparent',
                  borderLeft: isSel ? '4px solid var(--accent)' : '4px solid transparent',
                  boxShadow: isSel ? 'inset -1px 0 0 var(--border-subtle)' : 'none',
                  transition: 'background 100ms',
                }}
                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'var(--bg-sunken)'; }}
                onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{
                    display: 'inline-flex', padding: '2px 7px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                    background: opStyle(it.operation).bg, color: opStyle(it.operation).color,
                    letterSpacing: '0.04em',
                  }}>
                    {it.operation}
                  </span>
                  {isSel && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.05em' }}>VIEWING</span>}
                  <span style={{ fontSize: 12, color: 'var(--fg-3)', marginLeft: 'auto' }}>{ts(it.timestamp)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--fg-2)' }}>
                  <User size={11} style={{ color: 'var(--fg-3)' }} />
                  {it.by || 'System'}
                </div>
                <div style={{ fontSize: 12, color: isSel ? 'var(--accent)' : 'var(--fg-3)', fontWeight: isSel ? 600 : 400, marginTop: 2 }}>{it.table} · ID: {it.primaryKey}</div>
              </div>
              );
            })}
            {!loading && !error && items.length === 0 && (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--fg-3)', fontSize: 13.5 }}>No entries found</div>
            )}
          </div>

          {/* Detail pane */}
          <div style={{ overflowY: 'auto', padding: '20px 24px' }}>
            {!selected ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--fg-3)' }}>
                <FileText size={32} style={{ marginBottom: 10, opacity: 0.4 }} />
                <p style={{ margin: 0, fontSize: 13.5 }}>Select an entry to view details</p>
              </div>
            ) : (
              <>
                {/* Entry header */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{
                      display: 'inline-flex', padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                      background: opStyle(selected.operation).bg, color: opStyle(selected.operation).color,
                      letterSpacing: '0.04em',
                    }}>
                      {selected.operation}
                    </span>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{selected.table}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--fg-3)' }}>
                      <Clock size={13} /> {new Date(selected.timestamp).toLocaleString()}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--fg-3)' }}>
                      <User size={13} /> {selected.by || 'System'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--fg-3)' }}>
                      <Database size={13} /> ID: {selected.primaryKey}
                    </div>
                  </div>
                </div>

                {/* Changes */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fg-2)', paddingLeft: 12, borderLeft: '3px solid var(--accent)' }}>
                      Changes
                    </div>
                    <button
                      onClick={() => setShowRaw(v => !v)}
                      style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5, color: 'var(--fg-link)' }}
                    >
                      {showRaw ? 'View Formatted' : 'View Raw JSON'}
                    </button>
                  </div>

                  {!showRaw ? (
                    <DiffView data={selected.changedData} />
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <div>
                        <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Changed Data</div>
                        <pre style={{ margin: 0, padding: '12px 14px', background: 'var(--bg-sunken)', borderRadius: 'var(--radius-sm)', fontSize: 12, fontFamily: 'var(--font-mono)', overflowX: 'auto' }}>
                          {JSON.stringify(selected.changedData, null, 2)}
                        </pre>
                      </div>
                      <div>
                        <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Differences</div>
                        <pre style={{ margin: 0, padding: '12px 14px', background: 'var(--bg-sunken)', borderRadius: 'var(--radius-sm)', fontSize: 12, fontFamily: 'var(--font-mono)', overflowX: 'auto' }}>
                          {JSON.stringify(selected.differences, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
